// currentPlanDedupe — pure: guarantee AT MOST ONE pricing card reads
// "Current Plan".
//
// Gordon 2026-09-03: Billing showed BOTH Free and ProSuite as "Current Plan"
// for kelly@iyuga.net.
//
// How that renders today: each card decides for itself, in SubscriptionItem —
// `const activeItem = is_active || action === "ACTIVE"` — so if the packages
// payload marks two entries active, two cards claim to be the current plan.
// The card is also non-actionable when active (`disabled={activeItem}`), so a
// falsely-active Free card ALSO removes the user's "Downgrade to Free" action.
//
// This is a UI INVARIANT, deliberately independent of which side emits the
// duplicate: "current plan" is singular by definition, so the view must never
// render it twice no matter what the payload says. When the payload is already
// correct (exactly one active entry) this is a no-op and every card renders
// byte-identically.
//
// HONEST LIMIT: the live `GET /subscription/packages/` payload for
// kelly@iyuga.net was NOT measured here (the endpoint is 401 without her
// credentials, which this session does not hold). So this fixes the rendering
// invariant, and does NOT prove where the duplicate originates. If the BE is
// marking Free active alongside a paid tier, that is still worth fixing at the
// source — this guard just means it can no longer reach a user's screen.
//
// TIE-BREAK: keep the HIGHEST sub_tier. A user holding ProSuite also
// "has" Free in the trivial sense; the plan they are actually on — and paying
// for — is the highest one. Picking the lowest would tell a paying customer
// they are on the free plan, which is the worse failure.

/** True when this item is what SubscriptionItem would treat as the held tier. */
export function isActiveItem(item) {
  if (!item) return false;
  return Boolean(item.is_active) || item.action === "ACTIVE";
}

/**
 * Return the list with at most one active entry.
 *
 * @param {Array} items pricing items from the subscriptions API
 * @returns {Array} same order, same objects when nothing needed changing
 */
export function dedupeCurrentPlan(items) {
  const list = Array.isArray(items) ? items : [];
  const active = list.filter(isActiveItem);
  if (active.length <= 1) return list;

  // Highest sub_tier wins. A missing/garbled sub_tier sorts lowest so it can
  // never beat a real tier, but it can still win if it is the only candidate.
  const tierOf = (item) => {
    const n = Number(item?.sub_tier);
    return Number.isFinite(n) ? n : -1;
  };
  const winner = active.reduce((best, item) =>
    tierOf(item) > tierOf(best) ? item : best
  );

  return list.map((item) => {
    if (item === winner || !isActiveItem(item)) return item;
    // Strip BOTH signals — SubscriptionItem reads either one.
    const next = { ...item, is_active: false };
    if (next.action === "ACTIVE") {
      // Without a verb the CTA falls back to the tier-appropriate label
      // ("Downgrade to Free" for tier 0), restoring the action the false
      // "Current Plan" had disabled.
      delete next.action;
    }
    return next;
  });
}

export default dedupeCurrentPlan;
